import { AppCard } from "@/components/ui";
import { cn } from "@/utils";
import { ChevronRight } from "lucide-react";
import type { TReadinessCategory } from "./types";

const PERFORMANCE_COLORS: Record<string, { bar: string; badge: string }> = {
  POOR: { bar: "bg-amber-500", badge: "bg-amber-100 text-amber-800 border-amber-200" },
  FAIR: { bar: "bg-slate-500", badge: "bg-slate-100 text-slate-800 border-slate-200" },
  GOOD: { bar: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  AVERAGE: { bar: "bg-primary", badge: "bg-primary/10 text-primary border-primary/20" },
  EXCEPTIONAL: { bar: "bg-emerald-600", badge: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

const DEFAULT_PERFORMANCE = { bar: "bg-muted-foreground", badge: "bg-muted text-foreground border-border" };

const SCORE_MAX = 100;

type TReadinessCategoryCardProps = {
  categoryName: string;
  category: TReadinessCategory;
  onViewReasoning?: () => void;
};

function getPerformanceStyle(level: string | undefined) {
  if (!level) return DEFAULT_PERFORMANCE;
  const key = level.toUpperCase();
  return PERFORMANCE_COLORS[key] ?? DEFAULT_PERFORMANCE;
}

function isLowConfidence(confidence: string | undefined): boolean {
  if (!confidence) return false;
  return confidence.toLowerCase() === "low";
}

export function ReadinessCategoryCard({ categoryName, category, onViewReasoning }: TReadinessCategoryCardProps) {
  const score = category.score ?? 0;
  const performanceLevel = category.performance_level ?? "";
  const maturityLevel = category.maturity_level ?? "";
  const confidence = category.confidence;
  const hasReasoning =
    (category.reasoning?.trim().length ?? 0) > 0 || (category.score_explanation?.trim().length ?? 0) > 0;
  const style = getPerformanceStyle(performanceLevel);
  const lowConfidence = isLowConfidence(confidence);

  const percent = Math.min(100, Math.max(0, (score / SCORE_MAX) * 100));

  return (
    <AppCard
      as="button"
      type="button"
      onClick={hasReasoning ? onViewReasoning : undefined}
      className={cn(
        "flex w-full min-w-0 flex-col overflow-visible rounded-lg p-4 text-left transition-all duration-200 ease-in-out",
        "hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        hasReasoning && "cursor-pointer",
        !hasReasoning && "cursor-default",
        lowConfidence ? "border-dashed border-amber-300 opacity-90" : "border-border"
      )}
    >
      {/* Row 1: Category name + reasoning icon */}
      <div className="flex min-w-0 items-start justify-between gap-1">
        <h4 className="line-clamp-2 break-words text-[11px] font-semibold leading-tight text-foreground sm:text-xs">
          {categoryName}
        </h4>
        {hasReasoning && (
          <ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground/60" aria-hidden="true" />
        )}
      </div>

      {/* Row 2: Maturity badge + Score - reduced score size so it doesn't get cut off */}
      <div className="mt-2 flex min-w-0 items-center justify-between gap-2">
        <div className="min-w-0 flex-shrink-0">
          {maturityLevel && (
            <span className={cn("inline-block rounded-full border px-2 py-0.5 text-xs font-medium", style.badge)}>
              {maturityLevel}
            </span>
          )}
        </div>
        <span className="flex-shrink-0 text-base font-bold tabular-nums text-foreground sm:text-lg">{score}</span>
      </div>

      {/* Row 4: Progress bar */}
      <div className="mt-2 h-2 w-full min-w-0 overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all duration-500", style.bar)}
          style={{ width: `${percent}%` }}
        />
      </div>

      {/* Row 5: Performance level - slightly smaller */}
      {performanceLevel && <p className="mt-2 text-[10px] font-medium text-muted-foreground sm:text-xs">{performanceLevel}</p>}

      {/* Low confidence chip */}
      {lowConfidence && (
        <div className="mt-2 flex flex-col gap-0.5">
          <span className="inline-flex w-fit items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
            Low confidence
          </span>
          <p className="text-[10px] text-muted-foreground">Complete to-do items to improve</p>
        </div>
      )}

    </AppCard>
  );
}
